import {
  Component,
  effect,
  input,
  OnChanges,
  output,
  ViewChild,
  ChangeDetectionStrategy,
  Injector,
  OnInit,
  afterNextRender,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Modal } from 'flowbite';
import moment from 'moment';
import {
  getSetupDriverAppForm,
  getSetupGeneralForm,
  getSetupUserAppForm,
} from 'src/app/forms/setupForm';
import {
  IBase,
  IHolidayCalendar,
  IHolidayCalendarData,
  IRouteSetupDetail,
} from 'src/app/models';
import { InputType, ModalQueryParam, NotificationTypes, UserRole } from 'src/app/models/enums';
import {
  HolidayCalendarsService,
  LiteralService,
  ModalUrlService,
  StorageService,
  UtilsService,
} from 'src/app/services';
import { CheckboxComponent, ToggleComponent } from 'src/app/shared/input';
import { ModalHolidayCalendarComponent } from 'src/app/shared/modals/modal-holiday-calendar/modal-holiday-calendar.component';
import {
  routeSetupDetailConfigData,
  routeSetupDetailLocalTimeData,
} from 'src/app/store/initialisation/setup';
import { images } from 'src/images';

@Component({
  selector: 'app-setup-detail',
  templateUrl: './setup-detail.component.html',
  styleUrl: './setup-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SetupDetailComponent implements OnInit, OnChanges {
  readonly setupDetail = input<any>({} as IRouteSetupDetail);
  readonly setupObject = input<any>({} as IRouteSetupDetail);
  readonly newSetup = input<boolean>(false);
  readonly partialMode = input<boolean>(false);
  readonly show = input<boolean>(false);
  readonly edit = input<boolean>(false);
  readonly showChangeStatusEventEmitter = output<any>();
  readonly editActionEventEmitter = output();
  readonly resetSetupDetail = output<IRouteSetupDetail>();
  readonly createUpdateSetup = output<any>();
  readonly close = output();

  public setupDetailCopy: IRouteSetupDetail;

  public formGroup: FormGroup;
  public formType = 'general';

  public images = images;

  public setupForm: any[];

  public editStatus = false;

  public setupStatusList = [
    { id: 'activatedfemale', name: 'ACTIVATED' },
    { id: 'deactivatedfemale', name: 'DEACTIVATED' },
  ];

  public ToggleComponent = ToggleComponent;
  public CheckboxComponent = CheckboxComponent;

  @ViewChild(ModalHolidayCalendarComponent)
  holidayCalendarModalComponent: ModalHolidayCalendarComponent;
  public holidayCalendarModal: Modal;
  public userRole: UserRole;
  private holidayCalendarModalSetupId: number | undefined;
  private holidayCalendarModalRestorePending = false;

  constructor(
    public literalService: LiteralService,
    private holidayCalendarsService: HolidayCalendarsService,
    private storageService: StorageService,
    public utilsService: UtilsService,
    private modalUrlService: ModalUrlService,
    private activatedRoute: ActivatedRoute,
    private injector: Injector,
  ) {
    // Watch edit signal and update form when it changes
    effect(() => {
      const isEditing = this.edit();
      const isShown = this.show();
      if (isShown && this.formType) {
        this.updateSetupForm(this.formType);
      }
    });
  }

  ngOnInit() {
    this.consumeHolidayCalendarModalUrlState();
  }

  ngOnChanges(): void {
    this.closeHolidayCalendarModalOfAnotherSetup();
    this.restoreHolidayCalendarModal();
    if (!this.newSetup()) {
      this.setupDetailCopy = structuredClone(this.setupDetail());
    }
    const targetObject = this.newSetup()
      ? this.setupObject()
      : this.setupDetail();
    this.formGroup = new FormGroup({});

    // In partial mode, no fields are required
    const validators = this.partialMode() ? [] : [Validators.required];

    this.formGroup.setControl(
      'name',
      new FormControl(targetObject?.name || targetObject?.tag, validators),
    );
    this.formGroup.setControl(
      'minutesBeforeReady',
      new FormControl(targetObject?.config?.minutesBeforeReady, validators),
    );
    this.formGroup.setControl(
      'fromDate',
      new FormControl(targetObject?.fromDate, validators),
    );
    this.formGroup.setControl(
      'toDate',
      new FormControl(targetObject?.toDate, validators),
    );
    this.storageService.getRole().then((role) => (this.userRole = role));
    this.updateSetupForm(this.formType);
  }

  public async updateSetupForm(
    type: string,
    holidayCalendarData?: IHolidayCalendarData,
  ) {
    if (type === 'general') {
      this.formType = 'general';
      const targetObject = this.newSetup()
        ? this.setupObject()
        : this.setupDetail();
      await getSetupGeneralForm(
        targetObject,
        this.newSetup(),
        this.newSetup() ? false : this.edit(),
        this.literalService,
        this.holidayCalendarsService,
        holidayCalendarData,
      ).then((res: any) => {
        this.setupForm = this.filterFormForPartialMode(res);
      });
    } else if (type === 'userApp') {
      this.formType = 'userApp';
      await getSetupUserAppForm(
        this.newSetup() ? this.setupObject() : this.setupDetail(),
        this.newSetup(),
        this.edit(),
        this.literalService,
      ).then((res: any) => {
        this.setupForm = this.filterFormForPartialMode(res);
      });
    } else if (type === 'driverApp') {
      this.formType = 'driverApp';
      await getSetupDriverAppForm(
        this.newSetup() ? this.setupObject() : this.setupDetail(),
        this.newSetup(),
        this.edit(),
        this.literalService,
      ).then((res: any) => {
        this.setupForm = this.filterFormForPartialMode(res);
      });
    }
  }

  private filterFormForPartialMode(formData: any[]): any[] {
    if (!this.partialMode()) {
      return formData;
    }
    // In partial mode, disable the name field and mark all fields as not required
    return formData.map((field: any) => {
      if (field.edit) {
        field.edit.required = false;
        // Disable name field in partial mode
        if (field.edit.valueToChange === 'name') {
          field.edit.disabled = true;
        }
      }
      field.required = false;
      return field;
    });
  }

  async changeData(data: any, element?: any): Promise<void> {
    if (!element?.edit) return;

    const { valueToChange, inputType, disabled } = element.edit;
    const isRangeDate = inputType === InputType.RANGE_DATE;
    const isCheckbox = inputType === InputType.CHECKBOX;
    const isMultiSelect = Array.isArray(valueToChange) && !isRangeDate;

    const targetObject = this.newSetup()
      ? this.setupObject()
      : this.setupDetail();

    let valueChanged: any;

    if (valueToChange === 'holidayCalendars') {
      const options: any[] = data?.options ?? [];
      const selected: IBase[] = options.map((o: any) => {
        if (o.value && typeof o.value === 'object' && 'id' in o.value)
          return o.value as IBase;
        return { id: o.id, name: o.name ?? o.label } as IBase;
      });
      targetObject.holidayCalendars = selected;
      if (!Array.isArray(element.value)) element.value = [];
      element.value.length = 0;
      element.value.push(...selected);
      element.edit.selectedValues = element.value;
      return;
    }

    if (isMultiSelect) {
      this.setMultiSelectData(element);
      return;
    } else if (isCheckbox) {
      valueChanged = data.target.checked;
    } else {
      const value =
        typeof data === 'object' ? (data.target ?? data.options?.[0]) : data;
      valueChanged = this.setNormalData(
        value,
        typeof data === 'object',
        element,
      );

      if (isRangeDate) {
        const dateKey = data.target.id;
        if (dateKey) {
          targetObject[dateKey] = moment(valueChanged, 'DD/MM/YYYY').format(
            'YYYY-MM-DD',
          );
        }
        this.formGroup.get(valueToChange)?.setValue(targetObject[dateKey]);
        await this.updateSetupForm(this.formType);
        return;
      }
    }

    if (targetObject && valueToChange && !isRangeDate) {
      if (routeSetupDetailConfigData.includes(valueToChange)) {
        targetObject.config[valueToChange] =
          routeSetupDetailLocalTimeData.includes(valueToChange)
            ? this.formatLocalTime(valueChanged)
            : valueChanged;
      } else {
        targetObject[valueToChange] = valueChanged;
      }
      this.formGroup.get(valueToChange)?.setValue(valueChanged);
    }
    await this.updateSetupForm(this.formType);
  }

  private formatLocalTime(time: string): string | null {
    const parsedTime = moment(time, 'H:mm');
    return parsedTime.isValid() ? parsedTime.format('HH:mm') : null;
  }

  private setNormalData(data: any, isEvent = false, element: any) {
    if (isEvent) {
      return element.edit.inputType === InputType.TOGGLE
        ? data.checked
        : data.value;
    }
    return data;
  }

  private setMultiSelectData(element: any) {
    const selectableLabels = new Set(
      (element.edit?.values ?? []).map((item: any) => item.label),
    );
    element.edit.valueToChange.forEach((value: string) => {
      if (!selectableLabels.has(value)) return;
      let checked = false;
      if (
        element.edit?.selectedValues.some(
          (selectedValue: any) => selectedValue.label === value,
        )
      ) {
        checked = true;
      }
      const setup = this.newSetup() ? this.setupObject() : this.setupDetail();
      routeSetupDetailConfigData.includes(value)
        ? (setup.config[value] = checked)
        : (setup[value] = checked);
    });
  }

  editStatusClick() {
    this.editStatus = !this.editStatus;
  }

  changeStatus(data?: any) {
    if (data) {
      this.showChangeStatusEventEmitter.emit({
        id: this.setupDetail().id,
        value: data.value,
      });
    }
    this.editStatus = false;
  }

  editAction() {
    this.editActionEventEmitter.emit();
    this.resetSetupDetail.emit(this.setupDetailCopy);
  }

  cancel() {
    if (this.newSetup()) {
      this.closeDetails();
    } else {
      this.editActionEventEmitter.emit();
      this.resetSetupDetail.emit(this.setupDetailCopy);
    }
  }

  closeDetails() {
    this.close.emit();
  }

  onSubmit() {
    const data = {
      value: this.newSetup() ? this.setupObject() : this.setupDetail(),
      type: this.newSetup() ? 'create' : 'update',
    };
    this.createUpdateSetup.emit(data);
  }

  async scrollHolidayCalendars(event: any) {
    if (event.way === 'down') {
      const holidayCalendarData: IHolidayCalendarData =
        event.search !== ''
          ? { showMore: true, search: true, value: event.search }
          : { showMore: true };
      await this.updateSetupForm(this.formType, holidayCalendarData);
    }
  }

  async searchHolidayCalendars(event: any) {
    await this.updateSetupForm(this.formType, {
      showMore: false,
      search: true,
      value: event.search,
    });
  }

  getClientId(): number | undefined {
    return (this.newSetup() ? this.setupObject() : this.setupDetail())?.client
      ?.id;
  }

  openHolidayCalendarModal() {
    this.holidayCalendarModal = new Modal(this.holidayCalendarModalComponent.modalContainer().nativeElement, {
      closable: false,
      onHide: () => {
        this.holidayCalendarModalComponent?.resetState();
        this.modalUrlService.markClosed(ModalQueryParam.HOLIDAY_CALENDARS);
      },
    });
    this.holidayCalendarModalSetupId = this.setupDetail()?.id;
    this.holidayCalendarModalComponent.load(true);
    this.holidayCalendarModal.show();
    this.modalUrlService.markOpen(ModalQueryParam.HOLIDAY_CALENDARS);
  }

  private async consumeHolidayCalendarModalUrlState() {
    const isWizardSetup = this.activatedRoute.snapshot.data['wizard'] === true;
    const restoreRequested = await this.modalUrlService.consumeOpenState(ModalQueryParam.HOLIDAY_CALENDARS);
    this.holidayCalendarModalRestorePending = restoreRequested && !isWizardSetup && !this.partialMode();
    this.restoreHolidayCalendarModal();
  }

  private restoreHolidayCalendarModal() {
    if (!this.holidayCalendarModalRestorePending) return;
    if (this.newSetup()) {
      this.holidayCalendarModalRestorePending = false;
      return;
    }
    if (!this.setupDetail()?.id) return;
    this.holidayCalendarModalRestorePending = false;
    afterNextRender(() => this.openHolidayCalendarModal(), { injector: this.injector });
  }

  private closeHolidayCalendarModalOfAnotherSetup() {
    if (!this.holidayCalendarModal?.isVisible()) return;
    if (this.setupDetail()?.id !== this.holidayCalendarModalSetupId) this.holidayCalendarModal.hide();
  }

  async handleHolidayCalendarAction(event: any) {
    const calendarBody = event.holidayCalendar;
    const requestBody = {
      name: calendarBody.name,
      clientId: calendarBody.clientId,
      holidays: calendarBody.holidays ?? [],
    };
    try {
      if (event.isCreating) {
        const created =
          await this.holidayCalendarsService.createHolidayCalendar(requestBody);
        this.utilsService.showNotification(
          images.notifications.check,
          'setup.holidayCalendars',
          NotificationTypes.SUCCESS,
          'createSuccess.title',
          'createSuccess.text',
        );
        await this.holidayCalendarModalComponent?.onCreateSuccess(created);
        await this.updateSetupForm(this.formType);
      } else if (event.isEditing && calendarBody.id) {
        const updated =
          await this.holidayCalendarsService.updateHolidayCalendar(
            calendarBody.id,
            requestBody,
          );
        this.utilsService.showNotification(
          images.notifications.check,
          'setup.holidayCalendars',
          NotificationTypes.SUCCESS,
          'updateSuccess.title',
          'updateSuccess.text',
        );
        await this.holidayCalendarModalComponent?.onEditSuccess(updated);
        await this.updateSetupForm(this.formType);
      }
    } catch (error) {
      console.error('Error saving holiday calendar:', error);
      const errorKey = event.isCreating ? 'createError' : 'updateError';
      this.utilsService.showNotification(
        images.notifications.warning,
        'setup.holidayCalendars',
        NotificationTypes.DANGER,
        `${errorKey}.title`,
        `${errorKey}.text`,
      );
    }
  }

  async handleDeleteHolidayCalendar(calendar: IHolidayCalendar) {
    if (!calendar.id) return;
    try {
      await this.holidayCalendarsService.deleteHolidayCalendar(calendar.id);
      const targetObject = this.newSetup()
        ? this.setupObject()
        : this.setupDetail();
      if (targetObject?.holidayCalendars) {
        targetObject.holidayCalendars = targetObject.holidayCalendars.filter(
          (c: IHolidayCalendar) => c.id !== calendar.id,
        );
      }
      this.utilsService.showNotification(
        images.notifications.check,
        'setup.holidayCalendars',
        NotificationTypes.SUCCESS,
        'deleteSuccess.title',
        'deleteSuccess.text',
      );
      await this.holidayCalendarModalComponent?.onDeleteSuccess();
      await this.updateSetupForm(this.formType);
    } catch (error) {
      console.error('Error deleting holiday calendar:', error);
      this.utilsService.showNotification(
        images.notifications.warning,
        'setup.holidayCalendars',
        NotificationTypes.DANGER,
        'deleteError.title',
        'deleteError.text',
      );
    }
  }
}
